import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  List,
  Modal,
  Progress,
  Row,
  Segmented,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography
} from 'antd';
import {
  AndroidOutlined,
  ApiOutlined,
  AppleOutlined,
  AppstoreOutlined,
  BarChartOutlined,
  CheckCircleFilled,
  CloudServerOutlined,
  DatabaseOutlined,
  DesktopOutlined,
  ExclamationCircleOutlined,
  HistoryOutlined,
  LaptopOutlined,
  MessageOutlined,
  MobileOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  WarningOutlined,
  WechatOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { broadcastAPI, systemAdminDashboardV3API } from '../../services/api';
import SystemMonitor from '../../pages/settings/SystemMonitor';
import SystemSettings from '../../pages/settings/SystemSettings';
import SystemAdminOperationsOverviewV2 from './SystemAdminOperationsOverviewV2';
import './SystemAdminDashboardV3.css';

const { Title, Text, Paragraph } = Typography;
const VIEW_STORAGE_KEY = 'xlb:system-admin-dashboard-v3:view';
const validViews = new Set(['overview', 'operations', 'login', 'broadcasts', 'functions', 'health', 'settings', 'legacy']);

const terminalCatalog = [
  { key: 'web', name: '网页管理后台', icon: <AppstoreOutlined />, monitoring: 'pending' },
  { key: 'workbench', name: '教师工作台', icon: <LaptopOutlined />, monitoring: 'pending' },
  { key: 'wechat', name: '微信小程序', icon: <WechatOutlined />, monitoring: 'pending' },
  { key: 'ios', name: 'iOS App', icon: <AppleOutlined />, monitoring: 'pending' },
  { key: 'android', name: 'Android App', icon: <AndroidOutlined />, monitoring: 'pending' },
  { key: 'desktop', name: '桌面广播客户端', icon: <DesktopOutlined />, monitoring: 'monitored' }
];

const severityConfig = {
  critical: { color: 'error', label: '严重' },
  warning: { color: 'warning', label: '关注' },
  notice: { color: 'processing', label: '提示' }
};

const levelConfig = {
  ok: { label: '运行正常', className: '', color: '#12a36d' },
  warn: { label: '需要关注', className: 'warn', color: '#d97706' },
  danger: { label: '存在严重问题', className: 'danger', color: '#dc2626' }
};

function numberValue(value) {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) ? numeric : 0;
}

function percent(value) {
  return Number((numberValue(value) * 100).toFixed(1));
}

function formatTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString();
}

function MetricCard({ label, value, caption, color }) {
  return (
    <Card className="v3-metric-card" bordered={false}>
      <div className="v3-metric-label">{label}</div>
      <div className="v3-metric-value" style={color ? { color } : undefined}>{value}</div>
      <div className="v3-metric-caption">{caption}</div>
    </Card>
  );
}

function PanelTitle({ icon, children }) {
  return <div className="v3-panel-title">{icon}{children}</div>;
}

function ServiceCard({ icon, name, ok, value, caption }) {
  return (
    <div className="v3-service-card">
      <div className="v3-service-top">
        <div className="v3-service-name">{icon}{name}</div>
        <Tag color={ok ? 'success' : 'error'}>{ok ? '正常' : '异常'}</Tag>
      </div>
      <div className="v3-service-value">{value}</div>
      <Text type="secondary">{caption}</Text>
    </div>
  );
}

function RecentBroadcasts({ messages, loading, error, onReload, onSelect, compact }) {
  return (
    <Card
      className="v3-panel"
      bordered={false}
      title={<PanelTitle icon={<MessageOutlined />}>最近人工广播</PanelTitle>}
      extra={<Button size="small" icon={<ReloadOutlined />} loading={loading} onClick={onReload}>刷新</Button>}
    >
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      {loading && messages === null ? (
        <div style={{ textAlign: 'center', padding: 36 }}><Spin /></div>
      ) : !messages?.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无人工或即时广播" />
      ) : (
        <List
          dataSource={compact ? messages.slice(0, 5) : messages}
          pagination={!compact && messages.length > 10 ? { pageSize: 10, size: 'small' } : false}
          renderItem={(item) => (
            <List.Item
              style={{ cursor: 'pointer', paddingInline: 4 }}
              actions={[<Button type="link" size="small" key="detail" onClick={() => onSelect(item)}>查看</Button>]}
              onClick={() => onSelect(item)}
            >
              <List.Item.Meta
                title={<Space wrap><Text strong>{item.title || item.content?.slice(0, 30) || '未命名广播'}</Text><Tag>{item.senderType === 'teacher' ? '教师' : '管理员'}</Tag></Space>}
                description={<Text type="secondary">{item.senderName || '-'} · {item.schoolName || '-'} · {formatTime(item.createdAt)}</Text>}
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  );
}

function FunctionCoverage({ items, compact = false }) {
  const visibleItems = compact ? items.slice(0, 8) : items;
  return (
    <List
      dataSource={visibleItems}
      renderItem={(item) => (
        <List.Item>
          <div className="v3-function-row">
            <Text strong>{item.name}</Text>
            <span className="v3-function-source">{item.source}</span>
            <span className="v3-function-metric">{item.metric}</span>
            <Tag color={item.status === 'monitored' ? 'success' : 'default'}>
              {item.status === 'monitored' ? '已有真实监测' : '待接入监测'}
            </Tag>
          </div>
        </List.Item>
      )}
    />
  );
}

export default function SystemAdminDashboardV3({ legacyDashboard }) {
  const navigate = useNavigate();
  const [activeView, setActiveView] = useState(() => {
    const stored = window.localStorage.getItem(VIEW_STORAGE_KEY);
    return validViews.has(stored) ? stored : 'overview';
  });
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recentMessages, setRecentMessages] = useState(null);
  const [recentLoading, setRecentLoading] = useState(false);
  const [recentError, setRecentError] = useState('');
  const [selectedMessage, setSelectedMessage] = useState(null);

  const loadSnapshot = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await systemAdminDashboardV3API.getOverview();
      setSnapshot(response.data?.data || null);
    } catch (requestError) {
      setError(requestError.response?.data?.message || '运行快照加载失败');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const loadRecentMessages = useCallback(async () => {
    setRecentLoading(true);
    setRecentError('');
    try {
      const response = await broadcastAPI.getRecentManualMessages();
      setRecentMessages(response.data?.data?.messages || []);
    } catch (requestError) {
      setRecentError(requestError.response?.data?.message || '最近广播加载失败');
    } finally {
      setRecentLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSnapshot();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') loadSnapshot({ silent: true });
    }, 60_000);
    return () => window.clearInterval(timer);
  }, [loadSnapshot]);

  useEffect(() => {
    if ((activeView === 'overview' || activeView === 'broadcasts') && recentMessages === null && !recentLoading) {
      loadRecentMessages();
    }
  }, [activeView, loadRecentMessages, recentLoading, recentMessages]);

  const changeView = (value) => {
    setActiveView(value);
    window.localStorage.setItem(VIEW_STORAGE_KEY, value);
  };

  const options = useMemo(() => [
    { label: '运行总览', value: 'overview', icon: <SafetyCertificateOutlined /> },
    { label: '运营概览', value: 'operations', icon: <BarChartOutlined /> },
    { label: '登录与终端', value: 'login', icon: <MobileOutlined /> },
    { label: '广播与投递', value: 'broadcasts', icon: <MessageOutlined /> },
    { label: '功能运行', value: 'functions', icon: <AppstoreOutlined /> },
    { label: '系统健康', value: 'health', icon: <CloudServerOutlined /> },
    { label: '系统设置', value: 'settings', icon: <SettingOutlined /> },
    { label: '旧版仪表盘', value: 'legacy', icon: <HistoryOutlined /> }
  ], []);

  const monitor = snapshot?.monitor || {};
  const summary = snapshot?.summary || {};
  const incidents = snapshot?.incidents || [];
  const coverage = snapshot?.coverage || [];
  const api15m = monitor?.apiTraffic?.windows?.last15m || {};
  const broadcast = monitor?.broadcast || {};
  const desktop = monitor?.desktop || {};
  const teacherWorkbench = snapshot?.teacherWorkbench || {};
  const database = monitor?.database || {};
  const gateway = monitor?.services?.gateway || {};
  const redis = monitor?.services?.redis || {};
  const level = levelConfig[snapshot?.level] || levelConfig.ok;
  const monitoredPercent = summary.totalFunctions ? Math.round((numberValue(summary.monitored) / numberValue(summary.totalFunctions)) * 100) : 0;
  const desktopOnline = (desktop.versions || []).reduce((sum, item) => sum + numberValue(item.online15m), 0);

  const renderOverview = () => (
    <>
      <div className={`v3-status-strip ${level.className}`}>
        <div className="v3-status-strip-inner">
          <Space size={12} align="start">
            {snapshot?.level === 'danger' ? <ExclamationCircleOutlined style={{ color: level.color, fontSize: 21, marginTop: 2 }} /> : snapshot?.level === 'warn' ? <WarningOutlined style={{ color: level.color, fontSize: 21, marginTop: 2 }} /> : <CheckCircleFilled style={{ color: level.color, fontSize: 21, marginTop: 2 }} />}
            <div>
              <Text strong style={{ fontSize: 15 }}>当前平台状态：{level.label}</Text><br />
              <Text type="secondary">数据来自60秒运行快照；打开首页不会触发旧仪表盘的全量统计。</Text>
            </div>
          </Space>
          <Text type="secondary">快照更新：{formatTime(snapshot?.generatedAt)}</Text>
        </div>
      </div>

      <Row gutter={[12, 12]}>
        <Col xs={12} md={8} xl={4}><MetricCard label="严重问题" value={numberValue(summary.critical)} caption="需要立即处理" color={numberValue(summary.critical) > 0 ? '#dc2626' : '#12a36d'} /></Col>
        <Col xs={12} md={8} xl={4}><MetricCard label="关注问题" value={numberValue(summary.warning)} caption="异常与风险项" color={numberValue(summary.warning) > 0 ? '#d97706' : undefined} /></Col>
        <Col xs={12} md={8} xl={4}><MetricCard label="15分钟请求" value={numberValue(api15m.requests).toLocaleString()} caption={`4xx ${numberValue(api15m.clientErrors)} / 5xx ${numberValue(api15m.serverErrors)}`} /></Col>
        <Col xs={12} md={8} xl={4}><MetricCard label="API错误率" value={`${percent(api15m.errorRate)}%`} caption="最近15分钟" color={numberValue(api15m.serverErrors) > 0 ? '#d97706' : undefined} /></Col>
        <Col xs={12} md={8} xl={4}><MetricCard label="24小时广播" value={numberValue(broadcast.total24h).toLocaleString()} caption="实际广播消息" /></Col>
        <Col xs={12} md={8} xl={4}><MetricCard label="广播成功率" value={`${percent(broadcast.successRate)}%`} caption="已发送及部分送达" color={numberValue(broadcast.total24h) > 0 && numberValue(broadcast.successRate) < 0.95 ? '#dc2626' : '#12a36d'} /></Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} xl={15}>
          <Card className="v3-panel" bordered={false} title={<PanelTitle icon={<WarningOutlined />}>当前问题</PanelTitle>} extra={<Tag color={incidents.length ? 'warning' : 'success'}>{incidents.length ? `${incidents.length}项` : '无活动告警'}</Tag>}>
            {incidents.length ? (
              <List
                dataSource={incidents.slice(0, 8)}
                renderItem={(item) => {
                  const severity = severityConfig[item.severity] || severityConfig.notice;
                  return (
                    <List.Item actions={[item.route ? <Button type="link" size="small" key="route" onClick={() => navigate(item.route)}>处理</Button> : null].filter(Boolean)}>
                      <div className="v3-incident">
                        <span className={`v3-incident-marker ${item.severity}`} />
                        <div style={{ flex: 1 }}>
                          <Space wrap><Text strong>{item.title}</Text><Tag color={severity.color}>{severity.label}</Tag>{item.value !== undefined && <Tag>{item.value}</Tag>}</Space>
                          <div><Text type="secondary">{item.description}</Text></div>
                        </div>
                      </div>
                    </List.Item>
                  );
                }}
              />
            ) : (
              <div className="v3-empty-good"><CheckCircleFilled className="v3-empty-good-icon" /><div><Text strong>当前没有检测到活动问题</Text></div><Text type="secondary">系统仍会按60秒快照持续检查。</Text></div>
            )}
          </Card>
        </Col>
        <Col xs={24} xl={9}>
          <Card className="v3-panel" bordered={false} title={<PanelTitle icon={<CloudServerOutlined />}>核心服务</PanelTitle>} extra={<Button type="link" size="small" onClick={() => changeView('health')}>完整监控</Button>}>
            <Row gutter={[10, 10]}>
              <Col span={12}><ServiceCard icon={<ApiOutlined />} name="API" ok value={monitor?.services?.api?.pid ? '在线' : '检测中'} caption={`进程 ${monitor?.services?.api?.pid || '-'}`} /></Col>
              <Col span={12}><ServiceCard icon={<DatabaseOutlined />} name="MySQL" ok={database.ok !== false} value={`${numberValue(database.latencyMs)} ms`} caption={`${numberValue(database.threadsConnected)} / ${numberValue(database.maxConnections)} 连接`} /></Col>
              <Col span={12}><ServiceCard icon={<CloudServerOutlined />} name="Redis" ok={redis.ok !== false} value={`${numberValue(redis.latencyMs)} ms`} caption="缓存与队列" /></Col>
              <Col span={12}><ServiceCard icon={<MessageOutlined />} name="实时网关" ok={gateway.success !== false} value={gateway.success === false ? '异常' : '在线'} caption={`${numberValue(gateway.data?.openSockets)} 活跃连接`} /></Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} xl={14}>
          <Card className="v3-panel" bordered={false} title={<PanelTitle icon={<AppstoreOutlined />}>功能监测覆盖</PanelTitle>} extra={<Space><Text type="secondary">{numberValue(summary.monitored)} / {numberValue(summary.totalFunctions)}</Text><Progress type="circle" size={34} percent={monitoredPercent} showInfo={false} /></Space>}>
            <FunctionCoverage items={coverage} compact />
          </Card>
        </Col>
        <Col xs={24} xl={10}>
          <RecentBroadcasts compact messages={recentMessages} loading={recentLoading} error={recentError} onReload={loadRecentMessages} onSelect={setSelectedMessage} />
        </Col>
      </Row>
    </>
  );

  const renderLoginAndTerminals = () => (
    <>
      <Alert className="v3-section-note" type="warning" showIcon message="统一登录事件尚未全面接入" description="教师工作台仅统计累计下载、今日使用教师和累计使用教师；网页、微信、Apple与移动端登录仍保持待接入。" />
      <Row gutter={[14, 14]}>
        {terminalCatalog.map((terminal) => {
          const isDesktop = terminal.key === 'desktop';
          const isWorkbench = terminal.key === 'workbench';
          const isWorkbenchMonitored = isWorkbench && teacherWorkbench.available === true;
          const isMonitored = isDesktop || isWorkbenchMonitored;
          return (
            <Col xs={24} sm={12} xl={8} key={terminal.key}>
              <Card className="v3-terminal-card" bordered={false}>
                <div className="v3-terminal-head">
                  <Space><span style={{ color: isMonitored ? '#176bff' : '#637083', fontSize: 22 }}>{terminal.icon}</span><Text strong>{terminal.name}</Text></Space>
                  <Tag color={isMonitored ? 'success' : 'default'}>{isWorkbenchMonitored ? '已监测' : isDesktop ? '部分已监测' : '待接入'}</Tag>
                </div>
                {isWorkbenchMonitored ? (
                  <Row gutter={8}>
                    <Col span={8}><Statistic title="累计下载" value={numberValue(teacherWorkbench.downloadCount)} /></Col>
                    <Col span={8}><Statistic title="今日使用" value={numberValue(teacherWorkbench.activeTeachersToday)} /></Col>
                    <Col span={8}><Statistic title="累计使用" value={numberValue(teacherWorkbench.totalActiveTeachers)} /></Col>
                  </Row>
                ) : isDesktop ? (
                  <Row gutter={12}>
                    <Col span={12}><Statistic title="15分钟在线" value={desktopOnline} /></Col>
                    <Col span={12}><Statistic title="版本数量" value={(desktop.versions || []).length} /></Col>
                  </Row>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="等待统一登录与功能事件" />
                )}
              </Card>
            </Col>
          );
        })}
      </Row>
    </>
  );

  const renderBroadcasts = () => (
    <>
      <div className="v3-broadcast-kpis">
        <Row gutter={[20, 14]}>
          <Col xs={12} md={6}><Statistic title="24小时广播" value={numberValue(broadcast.total24h)} /></Col>
          <Col xs={12} md={6}><Statistic title="成功率" value={percent(broadcast.successRate)} suffix="%" /></Col>
          <Col xs={12} md={6}><Statistic title="发送成功" value={numberValue(broadcast.byStatus?.sent)} /></Col>
          <Col xs={12} md={6}><Statistic title="发送失败" value={numberValue(broadcast.byStatus?.failed)} /></Col>
        </Row>
      </div>
      <Alert className="v3-section-note" style={{ marginTop: 16 }} type="info" showIcon message="当前为已有真实口径" description="24小时广播来自受限时间窗口；最近广播固定读取最新120条并过滤定时广播。批次、目标数、确认送达、离线未送达和来源终端仍需接入统一广播指标。" />
      <RecentBroadcasts messages={recentMessages} loading={recentLoading} error={recentError} onReload={loadRecentMessages} onSelect={setSelectedMessage} />
    </>
  );

  const renderFunctions = () => (
    <>
      <Alert className="v3-section-note" type="info" showIcon message="功能监测台账" description="绿色项目已经连接真实监测数据；灰色项目尚未形成统一的使用量、成功率、失败量和积压量事件。后续接入后直接替换状态，不需要重做页面。" />
      <Card className="v3-panel" bordered={false} title={<PanelTitle icon={<AppstoreOutlined />}>全部核心功能</PanelTitle>} extra={<Tag color="blue">覆盖率 {monitoredPercent}%</Tag>}>
        <FunctionCoverage items={coverage} />
      </Card>
    </>
  );

  return (
    <div className="system-admin-v3">
      <div className="v3-header">
        <div>
          <div className="v3-eyebrow">Platform Operations</div>
          <Title level={2} className="v3-title">系统管理员运行驾驶舱</Title>
          <Text className="v3-subtitle">问题优先、真实数据、按需加载。用于发现系统故障、业务异常和监测盲区。</Text>
        </div>
        <div className="v3-header-actions">
          <span className={`v3-live-status ${level.className}`}><span className="v3-live-dot" />{level.label}</span>
          <Button icon={<ReloadOutlined />} loading={loading} onClick={() => loadSnapshot()}>刷新快照</Button>
        </div>
      </div>

      <div className="v3-tabs-shell">
        <Segmented size="large" block value={activeView} options={options} onChange={changeView} />
      </div>

      {error && <Alert className="v3-section-note" type="error" showIcon message="运行快照加载失败" description={error} action={<Button onClick={() => loadSnapshot()}>重试</Button>} />}
      {loading && !snapshot ? <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /><div style={{ marginTop: 14 }}>正在读取运行快照...</div></div> : (
        <>
          {activeView === 'overview' && renderOverview()}
          {activeView === 'operations' && <SystemAdminOperationsOverviewV2 monitoringSnapshot={snapshot} />}
          {activeView === 'login' && renderLoginAndTerminals()}
          {activeView === 'broadcasts' && renderBroadcasts()}
          {activeView === 'functions' && renderFunctions()}
          {activeView === 'health' && <SystemMonitor />}
          {activeView === 'settings' && <SystemSettings />}
          {activeView === 'legacy' && (
            <>
              <Alert className="v3-section-note" type="warning" showIcon message="当前显示原有系统管理员仪表盘" description="旧版组件及其统计逻辑保持原样；点击“运行总览”即可返回V3。" />
              {legacyDashboard}
            </>
          )}
        </>
      )}

      <Modal open={Boolean(selectedMessage)} title="广播内容详情" width={720} footer={<Button type="primary" onClick={() => setSelectedMessage(null)}>关闭</Button>} onCancel={() => setSelectedMessage(null)} destroyOnHidden>
        {selectedMessage && (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="标题">{selectedMessage.title || '-'}</Descriptions.Item>
            <Descriptions.Item label="发送人">{selectedMessage.senderName || '-'}</Descriptions.Item>
            <Descriptions.Item label="学校">{selectedMessage.schoolName || '-'}</Descriptions.Item>
            <Descriptions.Item label="发送目标">{selectedMessage.targetName || '-'}</Descriptions.Item>
            <Descriptions.Item label="发送时间">{formatTime(selectedMessage.createdAt)}</Descriptions.Item>
            <Descriptions.Item label="广播内容"><Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{selectedMessage.content || '-'}</Paragraph></Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}
